import {
  audiencesControllerCreate,
  audiencesControllerDelete,
  audiencesControllerGet,
  audiencesControllerList,
  audiencesControllerListMembers,
  audiencesControllerPreview,
  audiencesControllerUpdate,
} from '@/generated/korza-api-client';
import { fetchAllCampaigns } from '@/hooks/use-campaigns';
import type {
  AudienceDto,
  CampaignDto,
  CreateAudienceDto,
  FilterNodeDto,
  ListAudienceMembersResponseDto,
  PreviewAudienceResponseDto,
  UpdateAudienceDto,
} from '@/generated/korza-api-client/types.gen';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from './use-tenant';

export function useAudiences() {
  const tenant = useTenant();

  return useQuery<AudienceDto[]>({
    queryKey: ['audiences', tenant.slug],
    queryFn: async () => {
      const res = await audiencesControllerList({
        path: { tenantSlug: tenant.slug },
        throwOnError: true,
      });
      return res.data ?? [];
    },
    enabled: !!tenant.slug,
  });
}

export function useAudience(audienceId: string) {
  const tenant = useTenant();

  return useQuery<AudienceDto>({
    queryKey: ['audience', tenant.slug, audienceId],
    queryFn: async () => {
      const res = await audiencesControllerGet({
        path: { tenantSlug: tenant.slug, audienceId },
        throwOnError: true,
      });
      return res.data;
    },
    enabled: !!tenant.slug && !!audienceId,
  });
}

export function useAudienceMembers(
  audienceId: string,
  { limit, offset }: { limit: number; offset: number }
) {
  const tenant = useTenant();

  return useQuery<ListAudienceMembersResponseDto>({
    queryKey: ['audience-members', tenant.slug, audienceId, limit, offset],
    queryFn: async () => {
      const res = await audiencesControllerListMembers({
        path: { tenantSlug: tenant.slug, audienceId },
        query: { limit, offset },
        throwOnError: true,
      });
      return res.data;
    },
    enabled: !!tenant.slug && !!audienceId,
  });
}

export function useAudiencePreview(filters: FilterNodeDto | undefined) {
  const tenant = useTenant();

  return useQuery<PreviewAudienceResponseDto>({
    queryKey: [
      'audience-preview',
      tenant.slug,
      filters ? JSON.stringify(filters) : null,
    ],
    queryFn: async () => {
      const res = await audiencesControllerPreview({
        path: { tenantSlug: tenant.slug },
        body: { filters: filters! },
        throwOnError: true,
      });
      return res.data;
    },
    enabled: !!tenant.slug && !!filters,
  });
}

export function usePreviewAudience() {
  const tenant = useTenant();

  return useMutation({
    mutationFn: async (
      filters: FilterNodeDto
    ): Promise<PreviewAudienceResponseDto> => {
      const res = await audiencesControllerPreview({
        path: { tenantSlug: tenant.slug },
        body: { filters },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function useCreateAudience() {
  const tenant = useTenant();

  return useMutation({
    mutationFn: async (body: CreateAudienceDto): Promise<AudienceDto> => {
      const res = await audiencesControllerCreate({
        path: { tenantSlug: tenant.slug },
        body,
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function useUpdateAudience() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      audienceId,
      body,
    }: {
      audienceId: string;
      body: UpdateAudienceDto;
    }): Promise<AudienceDto> => {
      const res = await audiencesControllerUpdate({
        path: { tenantSlug: tenant.slug, audienceId },
        body,
        throwOnError: true,
      });
      return res.data;
    },
    onSuccess: (_data, { audienceId }) => {
      queryClient.invalidateQueries({ queryKey: ['audiences', tenant.slug] });
      queryClient.invalidateQueries({
        queryKey: ['audience', tenant.slug, audienceId],
      });
      queryClient.invalidateQueries({
        queryKey: ['audience-preview', tenant.slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['audience-members', tenant.slug, audienceId],
      });
    },
  });
}

export function useAudienceCampaigns(audienceId: string, enabled: boolean) {
  const tenant = useTenant();

  return useQuery<CampaignDto[]>({
    queryKey: ['audience-campaigns', tenant.slug, audienceId],
    queryFn: () => fetchAllCampaigns(tenant.slug, { audienceId }),
    enabled: !!tenant.slug && !!audienceId && enabled,
  });
}

export function useArchiveAudience() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (audienceId: string) => {
      const res = await audiencesControllerUpdate({
        path: { tenantSlug: tenant.slug, audienceId },
        body: { isArchived: true },
        throwOnError: true,
      });
      return res.data;
    },
    onSuccess: (_data, audienceId) => {
      queryClient.invalidateQueries({ queryKey: ['audiences', tenant.slug] });
      queryClient.invalidateQueries({
        queryKey: ['audience', tenant.slug, audienceId],
      });
    },
  });
}

export function useDeleteAudience() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (audienceId: string) => {
      const res = await audiencesControllerDelete({
        path: { tenantSlug: tenant.slug, audienceId },
        throwOnError: true,
      });
      return res.data;
    },
    onSuccess: (_data, audienceId) => {
      queryClient.invalidateQueries({ queryKey: ['audiences', tenant.slug] });
      queryClient.invalidateQueries({
        queryKey: ['audience', tenant.slug, audienceId],
      });
    },
  });
}
